import { useCallback, useEffect, useState } from 'react';

const KEY = 'hai_job_font_scale';
const ALLOWED = [0.9, 1.0, 1.15, 1.3];
const DEFAULT_SCALE = 1.0;

function readStored() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_SCALE;
    const num = parseFloat(raw);
    return ALLOWED.includes(num) ? num : DEFAULT_SCALE;
  } catch {
    return DEFAULT_SCALE;
  }
}

function apply(scale) {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty('--user-font-scale', String(scale));
}

/**
 * useFontScale, user-adjustable text-size preference.
 *
 * Returns:
 *   { scale, setScale, options }
 *
 * `setScale(n)` writes to localStorage and applies a CSS variable
 * `--user-font-scale` on <html>. CSS that uses calc(... * var(--user-font-scale, 1))
 * scales accordingly.
 *
 * Apply once at app mount; the hook is idempotent and safe to mount in multiple
 * places (e.g. each picker), but only the most-recently-rendered value wins.
 */
export function useFontScale() {
  const [scale, setScaleState] = useState(readStored);

  // Apply immediately on mount (covers SSR-like initial render or hot-reload).
  useEffect(() => {
    apply(scale);
  }, [scale]);

  const setScale = useCallback((next) => {
    if (!ALLOWED.includes(next)) return;
    try {
      localStorage.setItem(KEY, String(next));
    } catch {
      // ignore quota errors, in-memory state still works for this session
    }
    setScaleState(next);
  }, []);

  return {
    scale,
    setScale,
    options: [
      { value: 0.9, label: 'Klein' },
      { value: 1.0, label: 'Standaard' },
      { value: 1.15, label: 'Groot' },
      { value: 1.3, label: 'Extra groot' },
    ],
  };
}
